import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../lib/api";

const CurrencyContext = createContext(null);

const SYMBOLS = { AED: "AED", GBP: "\u00a3", EUR: "\u20ac", TRY: "\u20ba", USD: "$" };

// Currencies the user can pick as their global "home" display currency.
// Extend here if/when more home currencies are supported.
const HOME_CURRENCIES = ["EUR", "AED", "GBP"];

export function CurrencyProvider({ children }) {
  // Persist the user's home-currency choice so it survives reloads.
  const [mode, setModeState] = useState(() => {
    if (typeof window === "undefined") return "EUR";
    const saved = window.localStorage.getItem("equia.homeCurrency");
    return HOME_CURRENCIES.includes(saved) ? saved : "EUR";
  });
  const [rates, setRates] = useState({}); // EUR-based rates: { AED: 4.32, GBP: 0.87, ... }

  const setMode = useCallback((next) => {
    if (!HOME_CURRENCIES.includes(next)) return;
    setModeState(next);
    try {
      window.localStorage.setItem("equia.homeCurrency", next);
    } catch {
      // localStorage might be unavailable (private mode etc.) — non-fatal
    }
  }, []);

  const fetchRates = useCallback(async () => {
    try {
      const { data } = await api.get("/fx/rates?base=EUR");
      setRates(data.rates || {});
    } catch {
      // silent — will use last cached rates
    }
  }, []);

  useEffect(() => { fetchRates(); }, [fetchRates]);

  /** Convert an amount from `fromCurrency` to EUR */
  const toEur = useCallback((amount, fromCurrency) => {
    if (!amount || fromCurrency === "EUR") return amount || 0;
    const rate = rates[fromCurrency];
    return rate && rate !== 0 ? amount / rate : amount;
  }, [rates]);

  /** Convert an amount from EUR to `toCurrency` */
  const fromEur = useCallback((eurAmount, toCurrency) => {
    if (!eurAmount || toCurrency === "EUR") return eurAmount || 0;
    const rate = rates[toCurrency];
    return rate ? eurAmount * rate : eurAmount;
  }, [rates]);

  /** True when we hold a usable EUR rate for `currency` (EUR is always true) */
  const isConvertible = useCallback((currency) => {
    if (!currency || currency === "EUR") return true;
    return rates[currency] > 0;
  }, [rates]);

  /**
   * Display-ready value based on the global home currency (`mode`).
   * Always converts `amount` (in `assetCurrency`) into `mode`.
   * If `assetCurrency` has no FX rate, we CANNOT convert honestly — the value
   * is returned in its own native currency (never a misleading 1:1 EUR figure).
   * @param amount         - value in the asset's local currency
   * @param assetCurrency  - the asset's native currency (AED, GBP, EUR, …)
   * @returns {{ value: number, symbol: string, currency: string, converted: boolean }}
   */
  const display = useCallback((amount, assetCurrency) => {
    const value = amount || 0;
    if (assetCurrency === mode) {
      return { value, symbol: SYMBOLS[mode] || mode, currency: mode, converted: true };
    }
    if (!isConvertible(assetCurrency)) {
      return {
        value,
        symbol: SYMBOLS[assetCurrency] || assetCurrency,
        currency: assetCurrency,
        converted: false,
      };
    }
    // Convert via EUR pivot
    const eurValue = toEur(value, assetCurrency);
    const finalValue = mode === "EUR" ? eurValue : fromEur(eurValue, mode);
    return { value: finalValue, symbol: SYMBOLS[mode] || mode, currency: mode, converted: true };
  }, [mode, toEur, fromEur, isConvertible]);

  /** Format a display result as a string */
  const fmt = useCallback((amount, assetCurrency, decimals = 0) => {
    const d = display(amount, assetCurrency);
    const formatted = Math.abs(d.value) < 0.005 ? "0" :
      d.value.toLocaleString(undefined, { maximumFractionDigits: decimals });
    return `${d.symbol} ${formatted}`;
  }, [display]);

  /** Format with explicit +/- sign */
  const fmtSigned = useCallback((amount, assetCurrency, decimals = 0) => {
    const d = display(amount, assetCurrency);
    const abs = Math.abs(d.value);
    const formatted = abs < 0.005 ? "0" : abs.toLocaleString(undefined, { maximumFractionDigits: decimals });
    const sign = d.value >= 0 ? "+" : "-";
    return `${sign}${d.symbol} ${formatted}`;
  }, [display]);

  return (
    <CurrencyContext.Provider value={{
      mode,
      setMode,
      homeCurrencies: HOME_CURRENCIES,
      rates,
      toEur,
      fromEur,
      isConvertible,
      display,
      fmt,
      fmtSigned,
      fetchRates,
      SYMBOLS,
    }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within CurrencyProvider");
  return ctx;
}
